import {
  Box,
  Button,
  Flex,
  Paper,
  Text,
  TextInput,
  Table,
  Switch,
  Menu,
  Center,
  Stack,
  Loader,
} from '@mantine/core';
import {
  IconPlus,
  IconSearch,
  IconDots,
  IconFileDescription,
  IconEye,
  IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { useFundDocuments } from '../../hooks/useFundDocuments';
import { useCreateFundDocument } from '../../hooks/useCreateFundDocument';
import { useUpdateFundDocument } from '../../hooks/useUpdateFundDocument';
import { useDeleteFundDocument } from '../../hooks/useDeleteFundDocument';
import { useFileTemporaryLinkMutation } from 'src/data-capture/hooks/useFileTemporaryLinkMutation';
import { FundDocument } from '../../types';
import dayjs from 'dayjs';
import FundDocumentUploadModal, {
  FundDocumentUploadData,
  ReportTypeToLabel,
} from './FundDocumentUploadModal';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

type FundDocumentsProps = {
  fundId: string;
};

export default function FundDocuments({ fundId }: FundDocumentsProps) {
  const { documents, error, isError, isLoading } = useFundDocuments({ fundId });
  const { createFundDocument } = useCreateFundDocument();
  const { updateFundDocument } = useUpdateFundDocument();
  const { deleteFundDocument } = useDeleteFundDocument();
  const { createLink } = useFileTemporaryLinkMutation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredDocuments = documents.filter(doc =>
    doc.file.fileName.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleUpload = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.doc,.docx,.xls,.xlsx';
    fileInput.onchange = e => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files[0]) {
        const file = files[0];
        setSelectedFile(file);
        setIsModalOpen(true);
      }
    };
    fileInput.click();
  };

  const handleToggleLatest = async (document: FundDocument) => {
    try {
      await updateFundDocument(fundId, {
        ...document,
        isLatest: !document.isLatest,
      });
    } catch (error) {
      notifyAndLogError(
        'Error',
        'We were not able to toggle. Please try again or contact support.',
        error,
      );
    }
  };

  const handleViewDocument = async (document: FundDocument) => {
    const link = await createLink(document.file.id);
    window.open(link.url, '_blank');
  };

  const handleDeleteDocument = async (document: FundDocument) => {
    try {
      await deleteFundDocument(fundId, document.id);
    } catch (error) {
      notifyAndLogError(
        'Error',
        'We were not able to delete the fund document. Please try again or contact support.',
        error,
      );
    }
  };

  const handleModalSubmit = async (values: FundDocumentUploadData) => {
    try {
      if (selectedFile) {
        const newDocument: Omit<FundDocument, 'id'> = {
          file: {
            id: Date.now(),
            fileName: selectedFile.name,
            fileSize: selectedFile.size,
            createdAt: new Date().toISOString(),
            isFolder: false,
            isResolved: false,
            isFiled: false,
            fileType: selectedFile.type,
            isTemporary: false,
          },
          isLatest: values.isLatest,
          reportType: values.reportType || 'OTHER',
          reportDate: values.reportDate || new Date(),
        };
        await createFundDocument(fundId, selectedFile, newDocument);
        setSelectedFile(null);
        setIsModalOpen(false);
      }
    } catch (error) {
      notifyAndLogError(
        'Error',
        'We were not able to upload the document. Please try again or contact support.',
        error,
      );
    }
  };

  const handleModalClose = () => {
    setSelectedFile(null);
    setIsModalOpen(false);
  };

  const renderNoDocumentSection = () => {
    if (isLoading) {
      return (
        <Center>
          <Loader />
        </Center>
      );
    }

    if (isError) {
      return (
        <GenericErrorPage
          error={error}
          title="Failed to load documents"
          text="There was an error loading the documents. Please try again."
        />
      );
    }

    return (
      <Center h={300}>
        <Stack align="center" gap="xs">
          <Box
            w={54}
            h={54}
            bg="gray.1"
            style={{
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconFileDescription size={21} color="var(--mantine-color-gray-7)" />
          </Box>
          <Text size="md" fw={FONT_WEIGHTS.medium} c="gray.7" ta="center">
            Add documents to get the latest fund information for your suitability reports.
          </Text>
          <Button
            variant="primary"
            size="md"
            leftSection={<IconPlus size={16} />}
            onClick={handleUpload}
            mt="sm"
          >
            Upload document
          </Button>
        </Stack>
      </Center>
    );
  };

  return (
    <Paper variant="content" p="xxl">
      <Flex justify="space-between" align="center" mb="md">
        <Text size="displayXs" fw={FONT_WEIGHTS.semibold} c="gray.10">
          Fund documents
        </Text>
        {documents.length > 0 && (
          <Button
            variant="primary"
            size="md"
            leftSection={<IconPlus size={16} />}
            onClick={handleUpload}
          >
            Upload document
          </Button>
        )}
      </Flex>

      {documents.length > 0 && (
        <Box bg="gray.2" p="xxs" style={{ borderRadius: '12px' }} mb="md">
          <TextInput
            placeholder="Search documents..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            leftSection={<IconSearch size={16} />}
            variant="search"
          />
        </Box>
      )}

      {documents.length === 0 ? (
        renderNoDocumentSection()
      ) : (
        <Box>
          <Table variant="bare" verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>
                  <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.10">
                    Name
                  </Text>
                </Table.Th>
                <Table.Th>
                  <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.10">
                    Report type
                  </Text>
                </Table.Th>
                <Table.Th>
                  <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.10">
                    Report date
                  </Text>
                </Table.Th>
                <Table.Th>
                  <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="gray.10">
                    Set as latest
                  </Text>
                </Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredDocuments.map(document => (
                <Table.Tr key={document.id}>
                  <Table.Td>
                    <Box>
                      <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
                        {document.file.fileName}
                      </Text>
                      <Text size="xs" c="gray.6">
                        {new Intl.NumberFormat('en-GB').format(document.file.fileSize)} bytes •
                        Uploaded {dayjs(document.file.createdAt).format('D MMM YYYY')}
                      </Text>
                    </Box>
                  </Table.Td>
                  <Table.Td>
                    <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
                      {ReportTypeToLabel[document.reportType]}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
                      {document.reportDate ? dayjs(document.reportDate).format('D MMM YYYY') : '-'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Switch
                      checked={document.isLatest}
                      onChange={() => handleToggleLatest(document)}
                      size="sm"
                    />
                  </Table.Td>
                  <Table.Td align="right">
                    <Menu
                      transitionProps={{ transition: 'pop-top-right' }}
                      position="bottom-end"
                      width={160}
                      withinPortal
                    >
                      <Menu.Target>
                        <IconDots
                          color="#475467"
                          stroke={2}
                          size={20}
                          style={{ cursor: 'pointer' }}
                        />
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconEye size={16} />}
                          onClick={() => handleViewDocument(document)}
                        >
                          View
                        </Menu.Item>
                        <Menu.Divider />
                        <Menu.Item
                          leftSection={<IconTrash size={16} />}
                          color="red"
                          onClick={() => handleDeleteDocument(document)}
                        >
                          Delete
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      )}

      {selectedFile && (
        <FundDocumentUploadModal
          opened={isModalOpen}
          onClose={handleModalClose}
          file={selectedFile}
          onSubmit={handleModalSubmit}
        />
      )}
    </Paper>
  );
}
